import type { SavedGame } from '../sim/serialize';

/*
 * אחסון השמירה.
 *
 * IndexedDB ולא localStorage: localStorage סינכרוני (כתיבה חוסמת את הפריים), מוגבל
 * ל-~5MB, ומאחסן מחרוזות בלבד. שמירה של משחק גדול תעבור את הגבול ותיכשל בשקט
 * בדיוק ברגע הכי גרוע.
 *
 * כל הפעולות עוטפות שגיאה ומחזירות null: מצב פרטי, מכסת אחסון מלאה ודפדפן שחוסם
 * אחסון הם מצבים אמיתיים, ואף אחד מהם אינו סיבה להפיל משחק שרץ.
 */

const DB_NAME = 'crimson-front';
const DB_VERSION = 1;
const STORE = 'saves';
const AUTOSAVE_KEY = 'autosave';

export interface SaveSlot {
    readonly key: string;
    readonly savedAtMs: number;
    readonly mapId: string;
    readonly tick: number;
    readonly game: SavedGame;
}

function openDb(): Promise<IDBDatabase | null> {
    return new Promise((resolve) => {
        let request: IDBOpenDBRequest;
        try {
            request = indexedDB.open(DB_NAME, DB_VERSION);
        } catch {
            resolve(null);
            return;
        }
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        request.onblocked = () => resolve(null);
    });
}

async function withStore<T>(
    mode: IDBTransactionMode,
    body: (store: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
    const db = await openDb();
    if (!db) return null;
    return new Promise<T | null>((resolve) => {
        try {
            const tx = db.transaction(STORE, mode);
            const request = body(tx.objectStore(STORE));
            request.onsuccess = () => resolve(request.result as T);
            request.onerror = () => resolve(null);
            tx.oncomplete = () => db.close();
        } catch {
            resolve(null);
        }
    });
}

export async function writeAutosave(game: SavedGame, savedAtMs: number): Promise<boolean> {
    const slot: SaveSlot = {
        key: AUTOSAVE_KEY,
        savedAtMs,
        mapId: game.mapId,
        tick: game.tick,
        game,
    };
    const result = await withStore<IDBValidKey>('readwrite', (store) =>
        store.put(slot, AUTOSAVE_KEY),
    );
    return result !== null;
}

export async function readAutosave(): Promise<SaveSlot | null> {
    const slot = await withStore<SaveSlot | undefined>('readonly', (store) =>
        store.get(AUTOSAVE_KEY),
    );
    return slot ?? null;
}

export async function clearAutosave(): Promise<void> {
    await withStore('readwrite', (store) => store.delete(AUTOSAVE_KEY));
}
